import React from "react";
import DashboardLayout from "./DashboardLayout";

const pageShellStyle = {
  display: "grid",
  gap: "20px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
  borderRadius: "28px",
  padding: "28px",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
};

const panelGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "18px",
};

const panelStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

const adminFeatureRoutes = [
  { path: "/dashboard/student-application", title: "New Student Application", icon: "bi-person-vcard-fill", area: "Student", note: "Open a direct admin-side student application entry workspace." },
  { path: "/dashboard/payout", title: "Payout Request", icon: "bi-wallet2", area: "Payment", note: "Track franchise payout requests and approval flow." },
  { path: "/dashboard/deposit", title: "Load Wallet Request", icon: "bi-bank", area: "Payment", note: "Review and manage franchise load wallet requests and confirmations." },
  { path: "/dashboard/view-deposit", title: "View Money Request", icon: "bi-bank2", area: "Payment", note: "Review approved load money requests and their supporting details." },
  { path: "/dashboard/deduct", title: "New Deduct", icon: "bi-dash-circle-fill", area: "Payment", note: "Create new deduction entries against wallet or payment records." },
  { path: "/dashboard/view-deduct", title: "View Deduct", icon: "bi-list-check", area: "Payment", note: "Inspect deducted amounts and their reason trail." },
  { path: "/dashboard/online-payment", title: "Online Payment", icon: "bi-credit-card-2-front-fill", area: "Payment", note: "Monitor online payment records and settlement state." },
  { path: "/dashboard/statement", title: "View Statement", icon: "bi-file-earmark-bar-graph-fill", area: "Account Info", note: "See account-level transaction timeline and summaries." },
  { path: "/dashboard/new-exam", title: "New Exam", icon: "bi-patch-plus-fill", area: "Exam", note: "Create fresh exam records, session metadata, and timing setup." },
  { path: "/dashboard/view-exam", title: "View Exam", icon: "bi-ui-checks-grid", area: "Exam", note: "Browse exam list, publication state, and linked question counts." },
  { path: "/dashboard/new-mcq", title: "New MCQ Question", icon: "bi-ui-radios-grid", area: "Exam", note: "Add question bank entries and answer keys for online tests." },
  { path: "/dashboard/view-mcq", title: "View MCQ Question", icon: "bi-card-checklist", area: "Exam", note: "Inspect saved MCQ items, filters, and exam linkage." },
  { path: "/dashboard/csv-upload", title: "CSV Upload", icon: "bi-filetype-csv", area: "Exam", note: "Bulk import exam and question data through CSV files." },
  { path: "/dashboard/exam-result", title: "Online Exam Result", icon: "bi-trophy-fill", area: "Exam", note: "Review published result sheets and exam-wise performance." },
  { path: "/dashboard/marksheet", title: "Marksheet Download", icon: "bi-file-earmark-pdf-fill", area: "Certification", note: "Manage downloadable marksheet generation and access." },
  { path: "/dashboard/certificate", title: "Certificate Download", icon: "bi-award-fill", area: "Certification", note: "Prepare and release certificate download records." },
  { path: "/dashboard/new-grade", title: "Grade Point Setting", icon: "bi-bar-chart-steps", area: "Certification", note: "Define grade boundaries, points, and evaluation rules." },
  { path: "/dashboard/franchise-tc", title: "Franchise T&C", icon: "bi-file-earmark-text-fill", area: "Settings", note: "Maintain franchise terms, policies, and onboarding rules." },
  { path: "/dashboard/download-setting", title: "Download Page", icon: "bi-cloud-arrow-down-fill", area: "Settings", note: "Configure public download center content and visibility." },
  { path: "/dashboard/gallery", title: "Gallery", icon: "bi-images", area: "Settings", note: "Control gallery content, category-wise images, and presentation." },
  { path: "/dashboard/topbar-footer-setting", title: "Topbar & Footer", icon: "bi-layout-text-window-reverse", area: "Settings", note: "Edit public front-end topbar and footer content, contacts, and social links." },
  { path: "/dashboard/contact", title: "Contact Us", icon: "bi-telephone-fill", area: "Settings", note: "Manage contact information blocks and response channels." },
  { path: "/dashboard/new-message", title: "New Message", icon: "bi-chat-left-text-fill", area: "Message", note: "Compose and send fresh internal or broadcast messages." },
  { path: "/dashboard/view-message", title: "View Message", icon: "bi-chat-square-text-fill", area: "Message", note: "Read sent and received message history in one place." },
  { path: "/dashboard/message-request", title: "Message Request", icon: "bi-envelope-paper-fill", area: "Message", note: "Handle message requests and pending communication actions." },
  { path: "/dashboard/feedback-box", title: "Feedback Box", icon: "bi-star-fill", area: "Message", note: "Review student ratings, feedback, and approval actions." },
];

function FeaturePanel({ title, text, icon, tone }) {
  return (
    <div style={panelStyle}>
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: tone,
          color: "#7c2d3a",
          fontSize: "20px",
          marginBottom: "14px",
        }}
      >
        <i className={`bi ${icon}`}></i>
      </div>
      <h3 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: "18px" }}>{title}</h3>
      <p style={{ margin: 0, color: "#64748b", lineHeight: 1.7 }}>{text}</p>
    </div>
  );
}

function AdminFeaturePlaceholder({ title, icon, area, note }) {
  return (
    <DashboardLayout>
      <div style={pageShellStyle}>
        <section style={heroStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ maxWidth: "720px" }}>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
                {area} Workspace
              </p>
              <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>{title}</h1>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75 }}>
                {note}
              </p>
            </div>

            <div
              style={{
                width: "86px",
                height: "86px",
                borderRadius: "24px",
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "36px",
                flexShrink: 0,
              }}
            >
              <i className={`bi ${icon}`}></i>
            </div>
          </div>
        </section>

        <section style={panelGridStyle}>
          <FeaturePanel
            title="Page Created"
            text="This admin page is now available inside the master admin backend and ready for route-based navigation."
            icon="bi-check2-circle"
            tone="#fef3c7"
          />
          <FeaturePanel
            title="Next Wiring"
            text="API calls, forms, tables, filters, and approval logic can now be attached here based on your workflow."
            icon="bi-diagram-3-fill"
            tone="#dbeafe"
          />
          <FeaturePanel
            title="Responsive Shell"
            text="The page uses the same master admin layout, mobile sidebar drawer, and dashboard workspace styling."
            icon="bi-phone-fill"
            tone="#dcfce7"
          />
        </section>
      </div>
    </DashboardLayout>
  );
}

export { AdminFeaturePlaceholder, adminFeatureRoutes };
